/**
 * ==== STANDALONE:FARMHAND_LITE:START ====
 * Feature: Shared Animal Manager - multi-step wizard for adding/editing animals
 * Tier: Lite ($3/mo)
 * Dependencies: React, api.ts (API_BASE_URL), TapeWeightCalculator
 * Generalization: Replace RB API paths (/bridge-club/, /store-club/) with generic
 *   /api/animals/ path. Remove store/club branding. Core wizard logic is portable.
 * ============================================
 *
 * Shared Animal Manager Component
 * Used by both Store Club (/club/farm) and Bridge Club (/bridge-club/animals)
 *
 * Features:
 * - Multi-step wizard for adding animals
 * - Category-specific questions (dog sizes, poultry types, etc.)
 * - Smart feed consumption calculation
 * - Life stage selection by category
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../services/api';
import TapeWeightCalculator from './TapeWeightCalculator';
import { toTwoDecimals } from '../../utils/decimal';
import { parseApiError } from '../../utils/apiErrors';

// Helper to safely convert any value to a number
const toNumber = (val: any): number => {
  if (val === null || val === undefined) return 0;
  const num = typeof val === 'number' ? val : parseFloat(String(val));
  return isNaN(num) ? 0 : num;
};

// ===========================================
// TYPES
// ===========================================

export interface Animal {
  id: number;
  category: string;
  animal_type: string;
  animal_type_display?: string;
  animal_subtype?: string;
  name: string;
  breed: string;
  count: number;
  weight_lbs: number | null;
  activity_level: string;
  life_stage: string;
  dog_size?: string;
  housing_type?: string;
  production_level?: string;
  body_condition?: string;
  is_lactating?: boolean;
  is_pregnant?: boolean;
  is_breeding?: boolean;
  is_indoor?: boolean | null;
  daily_consumption?: number | null;
  monthly_consumption?: number | null;
  daily_consumption_lbs?: number | null;
  daily_consumption_cups?: number | null;  // For pets (dogs/cats)
  // True when the customer told us how much they feed, rather than taking our estimate.
  consumption_is_custom?: boolean;
  consumption_source?: {
    type: 'feed_product' | 'preferred_feed' | 'generic';
    product_id?: number;
    brand?: string;
    name?: string;
    display: string;
  } | null;
  // Age and growth tracking
  birth_date?: string | null;
  acquired_date?: string | null;
  acquired_age_months?: number | null;
  acquired_weight_lbs?: number | null;
  target_weight_lbs?: number | null;
  estimated_current_weight_lbs?: number | null;
  sex?: string;
  use_growth_curve?: boolean;
  // Computed fields from API
  age_display?: string | null;
  age_weeks?: number | null;
  days_to_target?: number | null;
}

interface FormData {
  category: string;
  animal_subtype: string;
  name: string;
  breed: string;
  count: number;
  weight_lbs: string;
  life_stage: string;
  activity_level: string;
  body_condition: string;
  housing_type: string;
  production_level: string;
  is_lactating: boolean;
  is_pregnant: boolean;
  is_breeding: boolean;
  is_indoor: boolean | null;
  dog_size: string;
  // Age and growth tracking
  birth_date: string;
  acquired_date: string;
  acquired_age_months: string;
  target_weight_lbs: string;
  sex: string;
  use_growth_curve: boolean;
  // OUR ESTIMATE OR THEIRS (Brad, 2026-09-25: "use our estimates or tell us how much you
  // feed?"). The estimate box is the place to say it, while the animal is being set
  // up - not later in the bin. 'custom' saves what they typed with consumption_is_custom,
  // so the server never recalculates over it; 'estimate' saves our number.
  feed_mode: 'estimate' | 'custom';
  custom_daily_feed: string;
}

export interface AnimalManagerProps {
  apiBase: string;  // "/store-club" or "/bridge-club"
  headerComponent?: React.ReactNode;
  onError?: (error: string) => void;
  onAnimalsChange?: (animals: Animal[]) => void;
  themeColor?: string;  // Primary accent color (default: #508590 teal, Bridge Club uses #B91C1C red)
  selectedButtonColor?: string;  // Color for selected option buttons (default: same as themeColor, Bridge Club uses #60A5FA light blue)
}

// ===========================================
// CONSTANTS
// ===========================================

const ANIMAL_CATEGORIES = [
  { value: 'poultry', label: 'Poultry', icon: '🐔' },
  { value: 'goat', label: 'Goats', icon: '🐐' },
  { value: 'sheep', label: 'Sheep', icon: '🐑' },
  { value: 'swine', label: 'Pigs', icon: '🐷' },
  { value: 'cattle', label: 'Cattle', icon: '🐄' },
  { value: 'horse', label: 'Horses', icon: '🐴' },
  { value: 'rabbit', label: 'Rabbits', icon: '🐰' },
  { value: 'dog', label: 'Dogs', icon: '🐕' },
  { value: 'cat', label: 'Cats', icon: '🐈' },
];

const POULTRY_SUBTYPES = [
  { value: 'laying_hens', label: 'Laying Hens' },
  { value: 'meat_birds', label: 'Meat Birds (Broilers)' },
  { value: 'pullets', label: 'Pullets' },
  { value: 'chicks', label: 'Chicks' },
  { value: 'mixed_flock', label: 'Mixed Flock' },
  { value: 'ducks', label: 'Ducks' },
  { value: 'turkeys', label: 'Turkeys' },
];

const GOAT_SUBTYPES = [
  { value: 'dairy', label: 'Dairy Goat' },
  { value: 'meat', label: 'Meat Goat (Boer)' },
  { value: 'fiber', label: 'Fiber Goat' },
  { value: 'pet', label: 'Pet/Companion' },
];

const SHEEP_SUBTYPES = [
  { value: 'meat_sheep', label: 'Meat Sheep' },
  { value: 'wool', label: 'Wool Sheep' },
  { value: 'dairy_sheep', label: 'Dairy Sheep' },
  { value: 'hair', label: 'Hair Sheep' },
];

const SWINE_SUBTYPES = [
  { value: 'feeder', label: 'Feeder Pigs' },
  { value: 'breeding_sow', label: 'Breeding Sow' },
  { value: 'boar', label: 'Boar' },
  { value: 'pet_pig', label: 'Pet Pig (Kunekune, etc.)' },
];

const CATTLE_SUBTYPES = [
  { value: 'beef', label: 'Beef Cattle' },
  { value: 'dairy_cow', label: 'Dairy Cattle' },
  { value: 'dual_purpose', label: 'Dual Purpose' },
];

const HORSE_SUBTYPES = [
  { value: 'light_riding', label: 'Light Riding' },
  { value: 'draft', label: 'Draft Horse' },
  { value: 'pony', label: 'Pony' },
  { value: 'mini', label: 'Miniature' },
];

const RABBIT_SUBTYPES = [
  { value: 'meat_rabbit', label: 'Meat Rabbit' },
  { value: 'fiber_rabbit', label: 'Fiber (Angora)' },
  { value: 'pet_rabbit', label: 'Pet' },
];

const DOG_SIZES = [
  { value: 'toy', label: 'Toy', desc: 'under 12 lbs' },
  { value: 'small', label: 'Small', desc: '12-25 lbs' },
  { value: 'medium', label: 'Medium', desc: '25-50 lbs' },
  { value: 'large', label: 'Large', desc: '50-100 lbs' },
  { value: 'giant', label: 'Giant', desc: '100+ lbs' },
];

const LIFE_STAGES_BY_CATEGORY: Record<string, Array<{ value: string; label: string }>> = {
  dog: [
    { value: 'puppy', label: 'Puppy' },
    { value: 'adult', label: 'Adult' },
    { value: 'senior', label: 'Senior' },
  ],
  cat: [
    { value: 'kitten', label: 'Kitten' },
    { value: 'adult', label: 'Adult' },
    { value: 'senior', label: 'Senior' },
  ],
  poultry: [
    { value: 'chick', label: 'Chicks (0-8 weeks)' },
    { value: 'grower', label: 'Grower (8-20 weeks)' },
    { value: 'laying', label: 'Laying' },
    { value: 'finishing', label: 'Finishing (meat)' },
  ],
  goat: [
    { value: 'kid', label: 'Kid' },
    { value: 'yearling', label: 'Yearling' },
    { value: 'adult', label: 'Adult' },
  ],
  sheep: [
    { value: 'kid', label: 'Lamb' },
    { value: 'yearling', label: 'Yearling' },
    { value: 'adult', label: 'Adult' },
  ],
  swine: [
    { value: 'piglet', label: 'Piglet' },
    { value: 'grower_pig', label: 'Grower (50-125 lbs)' },
    { value: 'finisher', label: 'Finisher (125-275 lbs)' },
    { value: 'breeding', label: 'Breeding Stock' },
  ],
  cattle: [
    { value: 'kid', label: 'Calf' },
    { value: 'yearling', label: 'Yearling' },
    { value: 'adult', label: 'Adult' },
  ],
  horse: [
    { value: 'foal', label: 'Foal' },
    { value: 'yearling', label: 'Yearling' },
    { value: 'adult', label: 'Adult' },
    { value: 'senior', label: 'Senior' },
  ],
  rabbit: [
    { value: 'kit', label: 'Kit' },
    { value: 'junior', label: 'Junior' },
    { value: 'adult', label: 'Adult' },
  ],
};

const ACTIVITY_LEVELS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'working', label: 'Working' },
];

const BODY_CONDITIONS = [
  { value: 'thin', label: 'Thin' },
  { value: 'ideal', label: 'Ideal' },
  { value: 'overweight', label: 'Overweight' },
];

const HOUSING_TYPES = [
  { value: 'free_range', label: 'Free Range' },
  { value: 'pastured', label: 'Pastured' },
  { value: 'confined', label: 'Confined/Coop' },
];

const PRODUCTION_LEVELS = [
  { value: 'high', label: 'High/Commercial' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'low', label: 'Low/Pet' },
];

const SEX_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'castrated', label: 'Castrated/Neutered' },
];

const initialFormData: FormData = {
  category: '',
  animal_subtype: '',
  name: '',
  breed: '',
  count: 1,
  weight_lbs: '',
  life_stage: '',
  activity_level: 'normal',
  body_condition: 'ideal',
  housing_type: '',
  production_level: '',
  is_lactating: false,
  is_pregnant: false,
  is_breeding: false,
  is_indoor: null,
  dog_size: '',
  // Age and growth tracking
  birth_date: '',
  acquired_date: '',
  acquired_age_months: '',
  target_weight_lbs: '',
  sex: 'unknown',
  use_growth_curve: true,
  feed_mode: 'estimate',
  custom_daily_feed: '',
};

// ===========================================
// FEED CALCULATION FUNCTIONS
// ===========================================

// Rounded to the DecimalField's two places BEFORE it goes anywhere: 0.15 * 6 is
// 0.8999999999999999 in JavaScript, and DRF rejects seventeen digits into a
// DecimalField(max_digits=6, decimal_places=2) - which is what blocked Tom's signup
// six times over. See utils/decimal.ts.
// The fields this form can highlight. Errors about anything else are spoken in the
// banner rather than dropped (utils/apiErrors.ts).
const ERROR_FIELDS = ['count', 'name', 'life_stage', 'weight_lbs', 'target_weight_lbs',
                      'birth_date', 'acquired_age_months'];

const calculateDailyFeed = (data: FormData): number => toTwoDecimals(rawDailyFeed(data));

const rawDailyFeed = (data: FormData): number => {
  const { category, animal_subtype, life_stage, count, weight_lbs, is_lactating, is_pregnant, production_level, activity_level } = data;
  const weight = parseFloat(weight_lbs) || 0;
  const qty = count || 1;

  switch (category) {
    case 'poultry':
      switch (life_stage) {
        case 'chick': return 0.05 * qty;
        case 'grower': return 0.15 * qty;
        case 'laying':
          if (production_level === 'high') return 0.30 * qty;
          if (production_level === 'moderate') return 0.27 * qty;
          return 0.25 * qty;
        case 'finishing': return 0.45 * qty;
        default: return 0.25 * qty;
      }

    case 'goat':
      let goatBase = weight * 0.035;
      if (life_stage === 'kid') goatBase = 1.5;
      if (is_lactating) goatBase += 1.0;
      if (is_pregnant) goatBase *= 1.15;
      return goatBase * qty;

    case 'sheep':
      let sheepBase = weight * 0.035;
      if (life_stage === 'kid') sheepBase = 1.5;
      if (is_lactating) sheepBase += 0.75;
      if (is_pregnant) sheepBase *= 1.15;
      return sheepBase * qty;

    case 'swine':
      switch (life_stage) {
        case 'piglet': return 1.5 * qty;
        case 'grower_pig': return 5.0 * qty;
        case 'finisher': return 7.0 * qty;
        case 'breeding':
          if (is_lactating) return 12.0 * qty;
          if (is_pregnant) return 6.0 * qty;
          return 5.0 * qty;
        default: return 5.0 * qty;
      }

    case 'cattle':
      let cattleBase = weight * 0.025;
      if (life_stage === 'kid') cattleBase = 8.0;
      if (animal_subtype === 'dairy_cow' && is_lactating) cattleBase *= 1.5;
      return cattleBase * qty;

    case 'horse':
      // Horse GRAIN: 0.3-0.5% body weight (3-5 lbs per 1000 lb horse)
      // NOT total diet (hay + grain), just the grain portion
      let horseBase = weight * 0.004;  // 0.4% = 4 lbs per 1000 lb horse
      if (activity_level === 'working') horseBase *= 1.25;  // Up to 5 lbs
      if (activity_level === 'high') horseBase *= 1.15;
      // Minimum 2 lbs for any adult horse
      if (weight > 500 && horseBase < 2) horseBase = 2;
      return horseBase * qty;

    case 'rabbit':
      let rabbitBase = 0.15;
      if (is_lactating) rabbitBase = 0.30;
      if (data.is_breeding) rabbitBase = 0.20;
      return rabbitBase * qty;

    case 'dog':
      return calculateDogCups(weight, activity_level, life_stage);

    case 'cat':
      return calculateCatCups(weight, activity_level, life_stage);

    default:
      return 0;
  }
};

const calculateDogCups = (weight: number, activity: string, stage: string): number => {
  // Calorie-based calculation for dogs
  // RER (Resting Energy Requirement) = 70 × (weight in kg)^0.75
  // For simplicity, using linear approximation: ~30 calories per lb for average adult
  const weightKg = weight / 2.205;

  // Calculate RER using standard formula
  let rer = 70 * Math.pow(weightKg, 0.75);

  // MER (Maintenance Energy Requirement) = RER × activity multiplier
  let activityMultiplier = 1.6; // Default for typical adult dog

  // Activity level adjustments
  if (activity === 'high' || activity === 'working') {
    activityMultiplier = 2.0; // Active/working dogs
  } else if (activity === 'low') {
    activityMultiplier = 1.2; // Inactive/couch potato
  }

  // Life stage adjustments
  if (stage === 'puppy') {
    activityMultiplier = 2.5; // Puppies need more calories for growth
  } else if (stage === 'senior') {
    activityMultiplier = 1.4; // Seniors need fewer calories
  }

  const dailyCalories = rer * activityMultiplier;

  // Convert calories to cups (assuming ~350 cal/cup for typical dry dog food)
  const cups = dailyCalories / 350;

  // Round to 1 decimal place, minimum 0.25 cups
  return Math.max(0.25, Math.round(cups * 4) / 4);
};

const calculateCatCups = (weight: number, activity: string, stage: string): number => {
  // Calorie-based calculation for cats
  // Cats need approximately 20 calories per pound for indoor cats
  // Outdoor/active cats need 35+ calories per pound
  const weightKg = weight / 2.205;

  // RER for cats: 70 × (weight in kg)^0.75
  let rer = 70 * Math.pow(weightKg, 0.75);

  // Activity multiplier for cats
  let activityMultiplier = 1.2; // Indoor cat default

  if (activity === 'high') {
    activityMultiplier = 1.6; // Active/outdoor cat
  } else if (activity === 'low') {
    activityMultiplier = 1.0; // Very sedentary
  }

  // Life stage adjustments
  if (stage === 'kitten') {
    activityMultiplier = 2.5; // Kittens need more calories
  } else if (stage === 'senior') {
    activityMultiplier = 1.1; // Seniors need fewer
  }

  const dailyCalories = rer * activityMultiplier;

  // Convert calories to cups (assuming ~400 cal/cup for typical dry cat food)
  const cups = dailyCalories / 400;

  // Round to nearest 0.1, minimum 0.2 cups
  return Math.max(0.2, Math.round(cups * 10) / 10);
};

const getRecommendedFeed = (category: string, subtype: string, lifeStage: string): string => {
  switch (category) {
    case 'poultry':
      if (lifeStage === 'chick') return 'Chick Starter';
      if (lifeStage === 'grower') return 'Grower Feed';
      if (lifeStage === 'laying') return 'Layer Feed';
      if (lifeStage === 'finishing') return 'Meat Bird Feed';
      return 'All Flock Feed';
    case 'goat':
      if (subtype === 'dairy') return 'Dairy Goat Chow';
      if (lifeStage === 'kid') return 'Kid Starter';
      return 'Goat Chow';
    case 'swine':
      if (lifeStage === 'piglet') return 'Pig Starter';
      if (lifeStage === 'grower_pig') return 'Pig Grower';
      if (lifeStage === 'finisher') return 'Pig Finisher';
      return 'Sow Feed';
    case 'horse':
      return 'Cavalry Horse Mash';
    default:
      return '';
  }
};

// ===========================================
// API FUNCTIONS
// ===========================================

// Custom error class to carry field-level errors
class ApiValidationError extends Error {
  fieldErrors: Record<string, string[]>;

  constructor(message: string, fieldErrors: Record<string, string[]>) {
    super(message);
    this.name = 'ApiValidationError';
    this.fieldErrors = fieldErrors;
  }
}

const createAnimalAPI = (apiBase: string) => {
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));

      // Parse Django REST Framework validation errors
      // DRF returns errors as { field_name: ["error message", ...], ... }
      const fieldErrors: Record<string, string[]> = {};
      let message = 'Request failed';

      if (errorData.detail) {
        // Simple detail message
        message = errorData.detail;
      } else if (typeof errorData === 'object') {
        // Field-level errors from DRF
        const errorFields = Object.keys(errorData).filter(k => k !== 'detail' && k !== 'non_field_errors');
        if (errorFields.length > 0) {
          errorFields.forEach(field => {
            const errors = errorData[field];
            if (Array.isArray(errors)) {
              fieldErrors[field] = errors.map(e => String(e));
            } else if (errors) {
              fieldErrors[field] = [String(errors)];
            }
          });
          message = `Validation errors on: ${errorFields.join(', ')}`;
        }
        if (errorData.non_field_errors) {
          message = Array.isArray(errorData.non_field_errors)
            ? errorData.non_field_errors.join(', ')
            : String(errorData.non_field_errors);
        }
      }

      throw new ApiValidationError(message, fieldErrors);
    }

    if (response.status === 204) return null;
    return response.json();
  };

  return {
    getAll: () => apiCall(`${apiBase}/animals/`),
    create: (data: any) => apiCall(`${apiBase}/animals/`, { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: any) => apiCall(`${apiBase}/animals/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: number) => apiCall(`${apiBase}/animals/${id}/`, { method: 'DELETE' }),
  };
};

// ===========================================
// MAIN COMPONENT
// ===========================================

// Error type for API responses
interface ApiError {
  message: string;
  fieldErrors: Record<string, string[]>;
}

const AnimalManager: React.FC<AnimalManagerProps> = ({
  apiBase,
  headerComponent,
  onError,
  onAnimalsChange,
  themeColor = '#508590',  // Default teal for Store Club, Bridge Club passes red
  selectedButtonColor,  // Defaults to themeColor if not specified
}) => {
  const effectiveSelectedColor = selectedButtonColor || themeColor;
  const styles = getStyles(themeColor, effectiveSelectedColor);
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState<Animal | null>(null);
  const [saving, setSaving] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [calculatedFeed, setCalculatedFeed] = useState<number>(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const api = React.useMemo(() => createAnimalAPI(apiBase), [apiBase]);

  useEffect(() => {
    loadAnimals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formData.category) {
      try {
        const feed = calculateDailyFeed(formData);
        setCalculatedFeed(feed);
      } catch (e) {
        setCalculatedFeed(0);
      }
    }
  }, [formData]);

  useEffect(() => {
    if (onAnimalsChange) {
      onAnimalsChange(animals);
    }
  }, [animals, onAnimalsChange]);

  const loadAnimals = async () => {
    setError(null);
    try {
      const data = await api.getAll();
      setAnimals(Array.isArray(data) ? data : []);
    } catch (err: any) {
      const errMsg = err?.message || 'Failed to load animals';
      setError(errMsg);
      if (onError) onError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setFormStep(1);
    setCalculatedFeed(0);
    setEditingAnimal(null);
    setShowAddForm(false);
    setFieldErrors({});
    setError(null);
  };

  // Helper to get error message for a field
  const getFieldError = (field: string): string | null => {
    return fieldErrors[field] || null;
  };

  // Helper to check if a field has an error
  const hasFieldError = (field: string): boolean => {
    return !!fieldErrors[field];
  };

  const handleEditAnimal = (animal: Animal) => {
    setFormData({
      category: animal.category,
      animal_subtype: animal.animal_subtype || '',
      name: animal.name || '',
      breed: animal.breed || '',
      count: animal.count || 1,
      weight_lbs: animal.weight_lbs?.toString() || '',
      life_stage: animal.life_stage || '',
      activity_level: animal.activity_level || 'normal',
      body_condition: animal.body_condition || 'ideal',
      housing_type: animal.housing_type || '',
      production_level: animal.production_level || '',
      is_lactating: animal.is_lactating || false,
      is_pregnant: animal.is_pregnant || false,
      is_breeding: animal.is_breeding || false,
      is_indoor: animal.is_indoor ?? null,
      dog_size: animal.dog_size || '',
      // Age and growth tracking
      birth_date: animal.birth_date || '',
      acquired_date: animal.acquired_date || '',
      acquired_age_months: animal.acquired_age_months?.toString() || '',
      target_weight_lbs: animal.target_weight_lbs?.toString() || '',
      sex: animal.sex || 'unknown',
      use_growth_curve: animal.use_growth_curve ?? true,
      feed_mode: animal.consumption_is_custom ? 'custom' : 'estimate',
      custom_daily_feed: animal.consumption_is_custom
        ? (toNumber(['dog', 'cat'].includes(animal.category)
            ? animal.daily_consumption_cups
            : (animal.daily_consumption_lbs ?? animal.daily_consumption)) || '').toString()
        : '',
    });
    setEditingAnimal(animal);
    setFormStep(2);
    setShowAddForm(true);
  };

  const handleSaveAnimal = async () => {
    setSaving(true);
    try {
      const isPet = ['dog', 'cat'].includes(formData.category);
      const dailyFeed = calculateDailyFeed(formData);

      const data: any = {
        category: formData.category,
        animal_type: formData.category,
        animal_subtype: formData.animal_subtype,
        name: formData.name || undefined,
        breed: formData.breed || undefined,
        count: isPet ? 1 : formData.count,
        weight_lbs: formData.weight_lbs ? parseFloat(formData.weight_lbs) : undefined,
        life_stage: formData.life_stage,
        activity_level: formData.activity_level,
        body_condition: formData.body_condition,
        housing_type: formData.housing_type,
        production_level: formData.production_level,
        is_lactating: formData.is_lactating,
        is_pregnant: formData.is_pregnant,
        is_breeding: formData.is_breeding,
        is_indoor: formData.is_indoor,
        dog_size: formData.dog_size,
        // Age and growth tracking
        birth_date: formData.birth_date || null,
        acquired_date: formData.acquired_date || null,
        acquired_age_months: formData.acquired_age_months ? parseInt(formData.acquired_age_months) : null,
        target_weight_lbs: formData.target_weight_lbs ? parseFloat(formData.target_weight_lbs) : null,
        sex: formData.sex,
        use_growth_curve: formData.use_growth_curve,
      };

      const custom = formData.feed_mode === 'custom' && parseFloat(formData.custom_daily_feed) > 0;
      const amount = custom ? parseFloat(formData.custom_daily_feed) : dailyFeed;
      if (isPet) {
        data.daily_consumption_cups = amount;
      } else {
        data.daily_consumption_lbs = amount;
      }
      // Theirs stays theirs: the server recalculates consumption when weight or stage
      // change, unless this is set.
      data.consumption_is_custom = custom;

      if (editingAnimal) {
        await api.update(editingAnimal.id, data);
      } else {
        await api.create(data);
      }

      await loadAnimals();
      resetForm();
    } catch (err: any) {
      // Field errors go next to their input; anything about a field this form does NOT
      // render - a computed one like the daily feed amount - is said in words in the
      // banner instead of leaving "fix the errors below" pointing at nothing.
      const parsed = parseApiError(err, {
        shownFields: ERROR_FIELDS,
        fallback: 'Failed to save animal',
      });
      setFieldErrors(parsed.fieldErrors);
      setError(parsed.message);
      if (onError) onError(parsed.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAnimal = async (id: number) => {
    if (!window.confirm('Remove this animal from your farm?')) return;
    try {
      await api.delete(id);
      await loadAnimals();
    } catch (err: any) {
      const errMsg = err?.message || 'Failed to delete animal';
      setError(errMsg);
      if (onError) onError(errMsg);
    }
  };

  const getSubtypes = () => {
    switch (formData.category) {
      case 'poultry': return POULTRY_SUBTYPES;
      case 'goat': return GOAT_SUBTYPES;
      case 'sheep': return SHEEP_SUBTYPES;
      case 'swine': return SWINE_SUBTYPES;
      case 'cattle': return CATTLE_SUBTYPES;
      case 'horse': return HORSE_SUBTYPES;
      case 'rabbit': return RABBIT_SUBTYPES;
      default: return [];
    }
  };

  const getLifeStages = () => {
    return LIFE_STAGES_BY_CATEGORY[formData.category] || [];
  };

  const isPet = ['dog', 'cat'].includes(formData.category);
  const needsWeight = ['dog', 'cat', 'goat', 'sheep', 'cattle', 'horse'].includes(formData.category);
  const needsCount = !isPet;
  const needsHousing = ['poultry'].includes(formData.category);
  const needsProduction = ['poultry', 'goat', 'sheep', 'cattle'].includes(formData.category) &&
    (formData.life_stage === 'laying' || formData.life_stage === 'adult');
  const canBeLactating = ['goat', 'sheep', 'cattle', 'swine', 'rabbit'].includes(formData.category) &&
    (formData.life_stage === 'adult' || formData.life_stage === 'breeding');

  const getAnimalIcon = (cat: string) => {
    return ANIMAL_CATEGORIES.find(c => c.value === cat)?.icon || '🐾';
  };

  if (loading) {
    return <div style={styles.loading}>Loading your farm...</div>;
  }

  // Form Step Rendering
  const renderFormStep = () => {
    // Step 1: Category Selection
    if (formStep === 1) {
      return (
        <div>
          <h3 style={styles.formTitle}>What kind of animal?</h3>
          <div style={styles.categoryGrid}>
            {ANIMAL_CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                style={{
                  ...styles.categoryButton,
                  backgroundColor: formData.category === cat.value ? effectiveSelectedColor : '#fff',
                  color: formData.category === cat.value ? '#fff' : '#333',
                  borderColor: formData.category === cat.value ? effectiveSelectedColor : '#e0e0e0',
                }}
                onClick={() => {
                  setFormData({ ...initialFormData, category: cat.value });
                  setFormStep(2);
                }}
              >
                <span style={styles.categoryIcon}>{cat.icon}</span>
                <span style={styles.categoryLabel}>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Step 2+: Details
    const subtypes = getSubtypes();
    const lifeStages = getLifeStages();

    return (
      <div>
        <div style={styles.formHeader}>
          <button style={styles.backButton} onClick={() => editingAnimal ? resetForm() : setFormStep(1)}>
            ← Back
          </button>
          <span style={styles.categoryBadge}>
            {getAnimalIcon(formData.category)} {ANIMAL_CATEGORIES.find(c => c.value === formData.category)?.label}
          </span>
        </div>

        {/* Subtype Selection */}
        {subtypes.length > 0 && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Type</label>
            <div style={styles.optionGrid}>
              {subtypes.map((st) => (
                <button
                  key={st.value}
                  style={{
                    ...styles.optionButton,
                    backgroundColor: formData.animal_subtype === st.value ? effectiveSelectedColor : '#fff',
                    color: formData.animal_subtype === st.value ? '#fff' : '#333',
                  }}
                  onClick={() => setFormData({ ...formData, animal_subtype: st.value })}
                >
                  {st.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Dog size selection */}
        {formData.category === 'dog' && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Size Category</label>
            <div style={styles.optionGrid}>
              {DOG_SIZES.map((sz) => (
                <button
                  key={sz.value}
                  style={{
                    ...styles.optionButton,
                    backgroundColor: formData.dog_size === sz.value ? effectiveSelectedColor : '#fff',
                    color: formData.dog_size === sz.value ? '#fff' : '#333',
                  }}
                  onClick={() => setFormData({ ...formData, dog_size: sz.value })}
                >
                  <span>{sz.label}</span>
                  <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>{sz.desc}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Count */}
        {needsCount && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>How many?</label>
            <input
              type="text"
              inputMode="numeric"
              value={formData.count}
              onChange={(e) => setFormData({ ...formData, count: e.target.value as any })}
              onBlur={(e) => {
                const val = parseInt(e.target.value) || 1;
                setFormData({ ...formData, count: Math.max(1, Math.min(10000, val)) });
              }}
              style={hasFieldError('count') ? styles.inputError : styles.input}
            />
            {getFieldError('count') && <span style={styles.fieldErrorText}>{getFieldError('count')}</span>}
          </div>
        )}

        {/* Name/Identifier */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>Name or ID (optional)</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            style={hasFieldError('name') ? styles.inputError : styles.input}
            placeholder={isPet ? 'Buddy' : 'e.g., Wilbur, Pig #1, Blue ear tag'}
          />
          {getFieldError('name') && <span style={styles.fieldErrorText}>{getFieldError('name')}</span>}
        </div>

        {/* Life Stage */}
        {lifeStages.length > 0 && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Life Stage</label>
            <div style={{
              ...styles.optionGrid,
              ...(hasFieldError('life_stage') ? { border: '2px solid #E73C3E', borderRadius: '8px', padding: '0.5rem' } : {})
            }}>
              {lifeStages.map((st) => (
                <button
                  key={st.value}
                  style={{
                    ...styles.optionButton,
                    backgroundColor: formData.life_stage === st.value ? effectiveSelectedColor : '#fff',
                    color: formData.life_stage === st.value ? '#fff' : '#333',
                  }}
                  onClick={() => setFormData({ ...formData, life_stage: st.value })}
                >
                  {st.label}
                </button>
              ))}
            </div>
            {getFieldError('life_stage') && <span style={styles.fieldErrorText}>{getFieldError('life_stage')}</span>}
          </div>
        )}

        {/* Weight */}
        {needsWeight && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>{isPet ? 'Weight (lbs)' : 'Average Weight (lbs)'}</label>
            <input
              type="text"
              inputMode="decimal"
              value={formData.weight_lbs}
              onChange={(e) => setFormData({ ...formData, weight_lbs: e.target.value })}
              onBlur={(e) => {
                const val = parseFloat(e.target.value) || 0;
                if (val > 0) {
                  setFormData({ ...formData, weight_lbs: Math.min(2500, val).toString() });
                }
              }}
              style={hasFieldError('weight_lbs') ? styles.inputError : styles.input}
              placeholder={isPet ? '50' : '150'}
            />
            {getFieldError('weight_lbs') && <span style={styles.fieldErrorText}>{getFieldError('weight_lbs')}</span>}
          </div>
        )}

        {/* Age Tracking Section for Livestock */}
        {!isPet && (
          <>
            {/* Growth Summary (when editing existing animal with growth data) */}
            {editingAnimal && (editingAnimal.age_display || editingAnimal.weight_lbs || editingAnimal.estimated_current_weight_lbs) && (
              <div style={styles.growthSummary}>
                <div style={styles.growthSummaryHeader}>
                  <span style={styles.growthIcon}>📈</span>
                  <span>Growth Tracking</span>
                </div>
                <div style={styles.growthStats}>
                  {editingAnimal.age_display && (
                    <div style={styles.growthStat}>
                      <span style={styles.growthStatLabel}>Current Age</span>
                      <span style={styles.growthStatValue}>{editingAnimal.age_display}</span>
                    </div>
                  )}
                  {/* Show actual weight if available, otherwise estimated */}
                  {editingAnimal.weight_lbs ? (
                    <div style={styles.growthStat}>
                      <span style={styles.growthStatLabel}>Weight (Measured)</span>
                      <span style={styles.growthStatValue}>
                        {Number(editingAnimal.weight_lbs).toFixed(1)} lbs
                      </span>
                    </div>
                  ) : editingAnimal.estimated_current_weight_lbs ? (
                    <div style={styles.growthStat}>
                      <span style={styles.growthStatLabel}>Est. Weight</span>
                      <span style={styles.growthStatValue}>
                        ~{Number(editingAnimal.estimated_current_weight_lbs).toFixed(0)} lbs
                      </span>
                    </div>
                  ) : null}
                  {editingAnimal.days_to_target != null && editingAnimal.days_to_target > 0 && (
                    <div style={styles.growthStat}>
                      <span style={styles.growthStatLabel}>To Target</span>
                      <span style={styles.growthStatValue}>{editingAnimal.days_to_target} days</span>
                    </div>
                  )}
                  {/* Calculate ADG using actual weight if available */}
                  {editingAnimal.age_weeks && editingAnimal.age_weeks > 0 && (editingAnimal.weight_lbs || editingAnimal.estimated_current_weight_lbs) && (
                    <div style={styles.growthStat}>
                      <span style={styles.growthStatLabel}>Avg Daily Gain</span>
                      <span style={styles.growthStatValue}>
                        {((Number(editingAnimal.weight_lbs || editingAnimal.estimated_current_weight_lbs) / (editingAnimal.age_weeks * 7)) || 0).toFixed(2)} lbs/day
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Birth Date or Acquired Age */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Age (optional - for growth tracking)</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <div style={{ flex: 1 }}>
                  <input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                    style={hasFieldError('birth_date') ? styles.inputError : styles.input}
                    placeholder="Birth Date"
                  />
                  <span style={{ fontSize: '0.75rem', color: hasFieldError('birth_date') ? '#E73C3E' : '#666' }}>
                    {getFieldError('birth_date') || 'Birth date'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', color: '#999' }}>or</div>
                <div style={{ flex: 1 }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.acquired_age_months}
                    onChange={(e) => setFormData({ ...formData, acquired_age_months: e.target.value })}
                    onBlur={(e) => {
                      const val = parseInt(e.target.value);
                      if (!isNaN(val) && val >= 0) {
                        setFormData({ ...formData, acquired_age_months: String(val) });
                      } else if (e.target.value && isNaN(val)) {
                        setFormData({ ...formData, acquired_age_months: '' });
                      }
                    }}
                    style={hasFieldError('acquired_age_months') ? styles.inputError : styles.input}
                    placeholder="Age in months"
                  />
                  <span style={{ fontSize: '0.75rem', color: hasFieldError('acquired_age_months') ? '#E73C3E' : '#666' }}>
                    {getFieldError('acquired_age_months') || 'Months old when acquired'}
                  </span>
                </div>
              </div>
            </div>

            {/* Sex for meat animals */}
            {['swine', 'goat', 'sheep', 'cattle'].includes(formData.category) && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Sex</label>
                <div style={styles.optionRow}>
                  {SEX_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      style={{
                        ...styles.optionButtonSmall,
                        backgroundColor: formData.sex === opt.value ? effectiveSelectedColor : '#fff',
                        color: formData.sex === opt.value ? '#fff' : '#333',
                      }}
                      onClick={() => setFormData({ ...formData, sex: opt.value })}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Target Weight for meat animals */}
            {['swine', 'goat', 'sheep', 'cattle'].includes(formData.category) && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Target Market Weight (lbs, optional)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.target_weight_lbs}
                  onChange={(e) => setFormData({ ...formData, target_weight_lbs: e.target.value })}
                  onBlur={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val) && val >= 1) {
                      setFormData({ ...formData, target_weight_lbs: String(val) });
                    } else if (e.target.value && (isNaN(val) || val < 1)) {
                      setFormData({ ...formData, target_weight_lbs: '' });
                    }
                  }}
                  style={hasFieldError('target_weight_lbs') ? styles.inputError : styles.input}
                  placeholder={formData.category === 'swine' ? '290' : formData.category === 'cattle' ? '1200' : '130'}
                />
                {getFieldError('target_weight_lbs') && <span style={styles.fieldErrorText}>{getFieldError('target_weight_lbs')}</span>}
              </div>
            )}

            {/* Tape Weight Calculator for Pigs */}
            {formData.category === 'swine' && (
              <div style={styles.tapeWeightSection}>
                <TapeWeightCalculator
                  animalId={editingAnimal?.id}
                  apiBase={apiBase}
                  themeColor={themeColor}
                  onWeightCalculated={(weight) => {
                    setFormData({ ...formData, weight_lbs: weight.toString() });
                  }}
                  onMeasurementSaved={async () => {
                    // Refresh animal data to get updated weight
                    await loadAnimals();
                    // Also update the editing animal if weight was updated
                    if (editingAnimal) {
                      const updated = animals.find(a => a.id === editingAnimal.id);
                      if (updated) {
                        setEditingAnimal(updated);
                        setFormData(prev => ({
                          ...prev,
                          weight_lbs: updated.weight_lbs?.toString() || prev.weight_lbs
                        }));
                      }
                    }
                  }}
                />
              </div>
            )}

            {/* Growth Curve Toggle */}
            <div style={styles.inputGroup}>
              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={formData.use_growth_curve}
                  onChange={(e) => setFormData({ ...formData, use_growth_curve: e.target.checked })}
                />
                Auto-adjust feed as animal grows (uses age to estimate weight)
              </label>
            </div>
          </>
        )}

        {/* Activity Level */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>Activity Level</label>
          <div style={styles.optionRow}>
            {ACTIVITY_LEVELS.filter(a => formData.category === 'horse' || a.value !== 'working').map((lvl) => (
              <button
                key={lvl.value}
                style={{
                  ...styles.optionButtonSmall,
                  backgroundColor: formData.activity_level === lvl.value ? effectiveSelectedColor : '#fff',
                  color: formData.activity_level === lvl.value ? '#fff' : '#333',
                }}
                onClick={() => setFormData({ ...formData, activity_level: lvl.value })}
              >
                {lvl.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body Condition */}
        {(isPet || needsWeight) && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Body Condition</label>
            <div style={styles.optionRow}>
              {BODY_CONDITIONS.map((bc) => (
                <button
                  key={bc.value}
                  style={{
                    ...styles.optionButtonSmall,
                    backgroundColor: formData.body_condition === bc.value ? effectiveSelectedColor : '#fff',
                    color: formData.body_condition === bc.value ? '#fff' : '#333',
                  }}
                  onClick={() => setFormData({ ...formData, body_condition: bc.value })}
                >
                  {bc.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Housing (for poultry) */}
        {needsHousing && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Housing Type</label>
            <div style={styles.optionRow}>
              {HOUSING_TYPES.map((h) => (
                <button
                  key={h.value}
                  style={{
                    ...styles.optionButtonSmall,
                    backgroundColor: formData.housing_type === h.value ? effectiveSelectedColor : '#fff',
                    color: formData.housing_type === h.value ? '#fff' : '#333',
                  }}
                  onClick={() => setFormData({ ...formData, housing_type: h.value })}
                >
                  {h.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Production Level */}
        {needsProduction && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Production Level</label>
            <div style={styles.optionRow}>
              {PRODUCTION_LEVELS.map((p) => (
                <button
                  key={p.value}
                  style={{
                    ...styles.optionButtonSmall,
                    backgroundColor: formData.production_level === p.value ? effectiveSelectedColor : '#fff',
                    color: formData.production_level === p.value ? '#fff' : '#333',
                  }}
                  onClick={() => setFormData({ ...formData, production_level: p.value })}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Lactating / Pregnant */}
        {canBeLactating && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Status</label>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={formData.is_lactating}
                  onChange={(e) => setFormData({ ...formData, is_lactating: e.target.checked })}
                />
                Lactating
              </label>
              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={formData.is_pregnant}
                  onChange={(e) => setFormData({ ...formData, is_pregnant: e.target.checked })}
                />
                Pregnant
              </label>
              {formData.category === 'rabbit' && (
                <label style={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={formData.is_breeding}
                    onChange={(e) => setFormData({ ...formData, is_breeding: e.target.checked })}
                  />
                  Breeding
                </label>
              )}
            </div>
          </div>
        )}

        {/* Indoor/Outdoor for cats */}
        {formData.category === 'cat' && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Indoor/Outdoor</label>
            <div style={styles.optionRow}>
              <button
                style={{
                  ...styles.optionButtonSmall,
                  backgroundColor: formData.is_indoor === true ? effectiveSelectedColor : '#fff',
                  color: formData.is_indoor === true ? '#fff' : '#333',
                }}
                onClick={() => setFormData({ ...formData, is_indoor: true })}
              >
                Indoor Only
              </button>
              <button
                style={{
                  ...styles.optionButtonSmall,
                  backgroundColor: formData.is_indoor === false ? effectiveSelectedColor : '#fff',
                  color: formData.is_indoor === false ? '#fff' : '#333',
                }}
                onClick={() => setFormData({ ...formData, is_indoor: false })}
              >
                Indoor/Outdoor
              </button>
            </div>
          </div>
        )}

        {/* Feed Calculation Result */}
        {calculatedFeed > 0 && (
          <div style={styles.feedCalc}>
            <div style={styles.feedCalcHeader}>
              {formData.feed_mode === 'custom' ? 'Daily feed, as you feed it' : 'Estimated Daily Feed'}
            </div>
            {formData.feed_mode === 'custom' ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={formData.custom_daily_feed}
                  onChange={e => setFormData({ ...formData, custom_daily_feed: e.target.value })}
                  onFocus={e => e.target.select()}
                  placeholder={toNumber(calculatedFeed).toFixed(1)}
                  aria-label={`Daily feed in ${isPet ? 'cups' : 'pounds'}`}
                  style={{ ...styles.input, width: 110, fontSize: '1.25rem', fontWeight: 'bold', textAlign: 'center', color: '#2e7d32' }}
                />
                <span style={{ ...styles.feedCalcValue, fontSize: '1.1rem' }}>{isPet ? 'cups' : 'lbs'}/day</span>
              </div>
            ) : (
              <div style={styles.feedCalcValue}>
                {toNumber(calculatedFeed).toFixed(1)} {isPet ? 'cups' : 'lbs'}/day
              </div>
            )}
            <div style={styles.feedCalcMonthly}>
              ~{((formData.feed_mode === 'custom' && parseFloat(formData.custom_daily_feed) > 0
                  ? parseFloat(formData.custom_daily_feed) : toNumber(calculatedFeed)) * 30).toFixed(0)} {isPet ? 'cups' : 'lbs'}/month
            </div>
            {/* Ours or theirs (Brad, 2026-09-25). Changeable again from the bin later. */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, feed_mode: 'estimate' })}
                style={{ ...styles.optionButtonSmall, minHeight: 40, padding: '6px 14px', fontSize: '0.9rem', backgroundColor: formData.feed_mode === 'estimate' ? effectiveSelectedColor : '#fff', color: formData.feed_mode === 'estimate' ? '#fff' : '#333' }}
              >
                Use our estimate
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, feed_mode: 'custom', custom_daily_feed: formData.custom_daily_feed || toNumber(calculatedFeed).toFixed(1) })}
                style={{ ...styles.optionButtonSmall, minHeight: 40, padding: '6px 14px', fontSize: '0.9rem', backgroundColor: formData.feed_mode === 'custom' ? effectiveSelectedColor : '#fff', color: formData.feed_mode === 'custom' ? '#fff' : '#333' }}
              >
                Tell us how much you feed
              </button>
            </div>
            <div style={{ fontSize: '0.8rem', color: '#4b5563', marginTop: 8 }}>
              {formData.feed_mode === 'custom'
                ? 'We use your number to know when the bin runs low. You can change it any time from the bin.'
                : 'An estimate from weight, age and activity. Feed differently? Tell us and we use your number.'}
            </div>
            {!isPet && getRecommendedFeed(formData.category, formData.animal_subtype, formData.life_stage) && (
              <div style={styles.feedRecommend}>
                Recommended: {getRecommendedFeed(formData.category, formData.animal_subtype, formData.life_stage)}
              </div>
            )}
          </div>
        )}

        {/* Save/Cancel Buttons */}
        <div style={styles.formActions}>
          <button style={styles.cancelButton} onClick={resetForm}>
            Cancel
          </button>
          <button
            style={{
              ...styles.saveButton,
              opacity: saving || !formData.life_stage ? 0.6 : 1,
            }}
            onClick={handleSaveAnimal}
            disabled={saving || !formData.life_stage}
          >
            {saving ? 'Saving...' : (editingAnimal ? 'Save Changes' : 'Add Animal')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.content}>
      {headerComponent}

      {error && <div style={styles.errorBanner}>{error}</div>}

      <div style={styles.header}>
        <h2 style={styles.heading}>My Farm</h2>
        {!showAddForm && (
          <button style={styles.addButton} onClick={() => setShowAddForm(true)}>
            + Add Animal
          </button>
        )}
      </div>

      {showAddForm && (
        <div style={styles.form}>
          {renderFormStep()}
        </div>
      )}

      {!showAddForm && animals.length === 0 && (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>🐾</div>
          <p style={styles.emptyText}>No animals yet</p>
          <button style={styles.addFirstButton} onClick={() => setShowAddForm(true)}>
            Add Your First Animal
          </button>
        </div>
      )}

      {!showAddForm && animals.length > 0 && (
        <div style={styles.animalList}>
          {animals.map((animal) => {
            const isAnimalPet = ['dog', 'cat'].includes(animal.category);
            return (
              <div key={animal.id} style={styles.animalCard}>
                <div style={styles.animalIcon}>{getAnimalIcon(animal.category)}</div>
                <div style={styles.animalInfo}>
                  <span style={styles.animalName}>
                    {animal.name ? (
                      <>
                        {animal.name}
                        {!isAnimalPet && (
                          <span style={styles.animalSubtitle}>
                            {' '}({animal.animal_subtype ?
                              SWINE_SUBTYPES.find(s => s.value === animal.animal_subtype)?.label ||
                              GOAT_SUBTYPES.find(s => s.value === animal.animal_subtype)?.label ||
                              SHEEP_SUBTYPES.find(s => s.value === animal.animal_subtype)?.label ||
                              CATTLE_SUBTYPES.find(s => s.value === animal.animal_subtype)?.label ||
                              animal.animal_subtype
                              : ANIMAL_CATEGORIES.find(c => c.value === animal.category)?.label || animal.category})
                          </span>
                        )}
                      </>
                    ) : (
                      animal.animal_type_display || ANIMAL_CATEGORIES.find(c => c.value === animal.category)?.label || animal.animal_type
                    )}
                  </span>
                  <span style={styles.animalDetails}>
                    {isAnimalPet ? (
                      `${animal.weight_lbs || '?'} lbs • ${animal.life_stage || 'adult'}`
                    ) : (
                      `${animal.count || 1} ${animal.animal_subtype ?
                        (SWINE_SUBTYPES.find(s => s.value === animal.animal_subtype)?.label ||
                         GOAT_SUBTYPES.find(s => s.value === animal.animal_subtype)?.label ||
                         SHEEP_SUBTYPES.find(s => s.value === animal.animal_subtype)?.label ||
                         CATTLE_SUBTYPES.find(s => s.value === animal.animal_subtype)?.label ||
                         animal.animal_subtype).toLowerCase()
                        : animal.category}`
                    )}
                  </span>
                  {/* Weight and age display for livestock */}
                  {!isAnimalPet && (
                    <span style={styles.animalAge}>
                      {animal.age_display && <>{animal.age_display}</>}
                      {/* Show actual weight if set, otherwise estimated */}
                      {animal.weight_lbs ? (
                        <> • {toNumber(animal.weight_lbs).toFixed(0)} lbs</>
                      ) : animal.estimated_current_weight_lbs ? (
                        <> • ~{toNumber(animal.estimated_current_weight_lbs).toFixed(0)} lbs est.</>
                      ) : null}
                    </span>
                  )}
                  {/* Days to target for meat animals */}
                  {!isAnimalPet && animal.days_to_target != null && animal.days_to_target > 0 && (
                    <span style={styles.animalTarget}>
                      {animal.days_to_target} days to market weight
                    </span>
                  )}
                  {/* Consumption display - cups for pets, lbs for livestock */}
                  {isAnimalPet ? (
                    animal.daily_consumption_cups != null && (
                      <span style={styles.animalConsumption}>
                        ~{toNumber(animal.daily_consumption_cups).toFixed(1)} cups/day
                        {animal.consumption_source?.type !== 'generic' && (
                          <span style={styles.feedSource}> ({animal.consumption_source?.brand})</span>
                        )}
                      </span>
                    )
                  ) : (
                    (animal.daily_consumption_lbs || animal.daily_consumption) != null && (
                      <span style={styles.animalConsumption}>
                        ~{toNumber(animal.daily_consumption_lbs || animal.daily_consumption).toFixed(1)} lbs/day
                      </span>
                    )
                  )}
                </div>
                <div style={styles.animalActions}>
                  <button style={styles.editButton} onClick={() => handleEditAnimal(animal)}>
                    Edit
                  </button>
                  <button style={styles.deleteButton} onClick={() => handleDeleteAnimal(animal.id)}>
                    ×
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ===========================================
// STYLES
// ===========================================

const getStyles = (themeColor: string, selectedButtonColor: string): { [key: string]: React.CSSProperties } => ({
  content: {
    padding: '1rem',
    paddingBottom: '5rem',
    maxWidth: '600px',
    margin: '0 auto',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#666',
  },
  errorBanner: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  heading: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  addButton: {
    backgroundColor: themeColor,
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  form: {
    backgroundColor: '#fff',
    padding: '1.5rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: '1.5rem',
  },
  formHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: themeColor,
    fontSize: '0.9375rem',
    cursor: 'pointer',
    padding: 0,
  },
  categoryBadge: {
    backgroundColor: '#f0f7f8',
    color: themeColor,
    padding: '0.375rem 0.75rem',
    borderRadius: '16px',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  formTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#333',
    margin: '0 0 1rem 0',
    textAlign: 'center',
  },
  categoryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.75rem',
  },
  categoryButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '1rem 0.5rem',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: '#fff',
  },
  categoryIcon: {
    fontSize: '1.75rem',
  },
  categoryLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  inputGroup: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#333',
    marginBottom: '0.5rem',
  },
  input: {
    width: '100%',
    padding: '0.875rem 1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    boxSizing: 'border-box',
  },
  inputError: {
    width: '100%',
    padding: '0.875rem 1rem',
    border: '2px solid #E73C3E',
    borderRadius: '8px',
    fontSize: '1rem',
    boxSizing: 'border-box',
    backgroundColor: '#fff5f5',
  },
  fieldErrorText: {
    color: '#E73C3E',
    fontSize: '0.75rem',
    marginTop: '0.25rem',
    display: 'block',
  },
  optionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '0.5rem',
  },
  optionRow: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  optionButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0.75rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: '#fff',
  },
  optionButtonSmall: {
    flex: 1,
    padding: '0.625rem 0.5rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: '#fff',
    minWidth: '60px',
  },
  checkboxGroup: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.9375rem',
    cursor: 'pointer',
  },
  feedCalc: {
    backgroundColor: '#e8f5e9',
    padding: '1rem',
    borderRadius: '12px',
    textAlign: 'center',
    marginTop: '1rem',
    marginBottom: '1rem',
  },
  feedCalcHeader: {
    fontSize: '0.75rem',
    color: '#388e3c',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '0.25rem',
  },
  feedCalcValue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#2e7d32',
  },
  feedCalcMonthly: {
    fontSize: '0.875rem',
    color: '#388e3c',
    marginTop: '0.25rem',
  },
  feedRecommend: {
    fontSize: '0.8125rem',
    color: themeColor,
    marginTop: '0.5rem',
    fontWeight: 500,
  },
  formActions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1.5rem',
  },
  cancelButton: {
    flex: 1,
    backgroundColor: 'transparent',
    color: '#666',
    border: '2px solid #e0e0e0',
    padding: '0.875rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  saveButton: {
    flex: 2,
    backgroundColor: themeColor,
    color: '#fff',
    border: 'none',
    padding: '0.875rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '3rem',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  emptyText: {
    color: '#666',
    margin: '0 0 1rem 0',
  },
  addFirstButton: {
    backgroundColor: themeColor,
    color: '#fff',
    border: 'none',
    padding: '0.875rem 1.5rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  animalList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  animalCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  animalIcon: {
    fontSize: '2rem',
  },
  animalInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
  },
  animalName: {
    fontWeight: 600,
    color: '#333',
    fontSize: '1rem',
  },
  animalSubtitle: {
    fontWeight: 400,
    color: '#666',
    fontSize: '0.875rem',
  },
  animalDetails: {
    fontSize: '0.8125rem',
    color: '#666',
  },
  animalConsumption: {
    fontSize: '0.75rem',
    color: themeColor,
    fontWeight: 500,
  },
  feedSource: {
    fontSize: '0.6875rem',
    color: '#666',
    fontWeight: 400,
  },
  animalAge: {
    fontSize: '0.75rem',
    color: '#6b7280',
    fontStyle: 'italic',
  },
  animalTarget: {
    fontSize: '0.7rem',
    color: '#16a34a',
    fontWeight: 500,
  },
  tapeWeightSection: {
    marginTop: '1rem',
    marginBottom: '1rem',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
  },
  growthSummary: {
    marginBottom: '1.25rem',
    padding: '1rem',
    backgroundColor: '#f0fdf4',
    borderRadius: '12px',
    border: '1px solid #86efac',
  },
  growthSummaryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.75rem',
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: '#166534',
  },
  growthIcon: {
    fontSize: '1.25rem',
  },
  growthStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '0.75rem',
  },
  growthStat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
  },
  growthStatLabel: {
    fontSize: '0.6875rem',
    color: '#4ade80',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  growthStatValue: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#166534',
  },
  animalActions: {
    display: 'flex',
    gap: '0.5rem',
  },
  editButton: {
    backgroundColor: 'transparent',
    color: themeColor,
    border: `1px solid ${themeColor}`,
    padding: '0.375rem 0.75rem',
    borderRadius: '6px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  deleteButton: {
    width: '28px',
    height: '28px',
    backgroundColor: '#ffebee',
    color: '#E73C3E',
    border: 'none',
    borderRadius: '50%',
    fontSize: '1.25rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default AnimalManager;
// ==== STANDALONE:FARMHAND_LITE:END ====
